import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  GraduationCap, Award, Clock, Users, CheckCircle2, ArrowRight,
  Target, Brain, Briefcase, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function ExecutiveProfessionalMasters() {
  const programHighlights = [
    { icon: Clock, label: "Duration", value: "12-18 Months" },
    { icon: Users, label: "Cohort Size", value: "20-25 Executives" },
    { icon: Globe, label: "Format", value: "Hybrid Learning" },
    { icon: Award, label: "Credential", value: "Executive Masters" }
  ];

  const curriculum = [
    {
      phase: "Foundation",
      duration: "Months 1-4",
      modules: [
        "Workforce Intelligence Fundamentals",
        "Organizational Diagnostics",
        "Leadership in the AI Era",
        "Data-Driven Decision Making"
      ]
    },
    {
      phase: "Application",
      duration: "Months 5-10",
      modules: [
        "AI Integration Strategy",
        "Workforce Transformation",
        "Change Management Excellence",
        "Performance Analytics"
      ]
    },
    {
      phase: "Capstone",
      duration: "Months 11-18",
      modules: [
        "Executive Capstone Project",
        "Organizational Implementation",
        "Leadership Presentation",
        "Peer Review & Assessment"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="executive-masters-page">
      {/* Hero Section */}
      <section className="bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/programs" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Programs
            </Link>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-sm font-medium mb-6">
                  <GraduationCap className="w-4 h-4" />
                  Executive Education
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6">
                  Executive Professional Masters
                </h1>
                <p className="text-lg text-white/80 leading-relaxed mb-8">
                  Transform your organization's workforce capabilities through our flagship executive program. 
                  Designed for senior leaders ready to drive AI-powered workforce transformation.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/admissions">
                    <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-12 px-8">
                      Apply Now
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="h-12 px-8 border-white/30 text-white hover:bg-white/10">
                      Request Information
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {programHighlights.map((item, index) => (
                  <Card key={index} className="bg-white/10 border-white/20">
                    <CardContent className="p-5 text-center">
                      <item.icon className="w-8 h-8 mx-auto mb-2 text-white/70" />
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{item.label}</p>
                      <p className="text-white font-semibold">{item.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Program Curriculum
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              A comprehensive curriculum designed to develop strategic workforce intelligence leadership capabilities.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {curriculum.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white">
                  <CardContent className="p-6">
                    <div className="text-center mb-6">
                      <span className="inline-block px-3 py-1 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-sm font-medium mb-2">
                        {phase.duration}
                      </span>
                      <h3 className="font-heading text-xl font-bold" style={{ color: '#991B1B' }}>
                        {phase.phase} Phase
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {phase.modules.map((module, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                          <span className="text-[#1E3A8A] text-sm">{module}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
            Begin Your Executive Journey
          </h2>
          <p className="text-lg text-[#1E3A8A] mb-8">
            Join the next cohort of executive leaders transforming their organizations through workforce intelligence.
          </p>
          <Link to="/admissions">
            <Button size="lg" className="bg-[#991B1B] text-white hover:bg-[#7F1D1D] h-12 px-8">
              Apply Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
